/* ===================================================================
   garage-modal-v2.js — Controller for the redesigned Garage Modal
   Dependencies: global-library.js (getSearchTerms, setSearchTerms,
     moveVehicleToFront, generateCollectionHandle, setFitmentObject,
     updateEasysearchLocalStorageOrNavigate, handleVinDecode,
     fetchBumpersLicenseToVin, cleanLicensePlate, handleVinChange,
     handleLicenseChange, handleStateSelectChange, logGarageEventToSheets,
     updateSearchTerms, syncCustomEasySearchButtons)
   =================================================================== */

/* Controller gate — tells legacy garage-script to yield polling & nav */
window.__GARAGE_CONTROLLER__ = "v2";

(function () {
	"use strict";

	/* ---- Selectors ---- */
	const SEL = {
		overlay: "[data-gv2-overlay]",
		modal: "[data-gv2-modal]",
		close: "[data-gv2-close]",
		left: "[data-gv2-left]",
		right: "[data-gv2-right]",
		// Views
		garageView: '[data-gv2-view="garage"]',
		addView: '[data-gv2-view="add"]',
		vehicleList: "[data-gv2-vehicle-list]",
		addVehicleBtn: "[data-gv2-add-vehicle]",
		backBtn: "[data-gv2-back-to-garage]",
		formTitle: "[data-gv2-form-title]",
		// Tabs
		tabs: "[data-gv2-tabs]",
		tab: "[data-gv2-tab]",
		panel: "[data-gv2-panel]",
		// Manual
		easysearchSlot: "[data-gv2-easysearch-slot]",
		manualSearch: "[data-gv2-manual-search]",
		// VIN
		vinInput: "[data-gv2-vin-input]",
		vinSubmit: "[data-gv2-vin-submit]",
		vinError: "[data-gv2-vin-error]",
		// License
		licenseState: "[data-gv2-license-state]",
		licensePlate: "[data-gv2-license-plate]",
		licenseSubmit: "[data-gv2-license-submit]",
		licenseError: "[data-gv2-license-error]",
		// Preview
		preview: "[data-gv2-preview]",
		previewImage: "[data-gv2-vehicle-image]",
		spinner: "[data-gv2-spinner]",
		previewTitle: "[data-gv2-preview-title]",
		previewVin: "[data-gv2-preview-vin]",
		previewPaint: "[data-gv2-preview-paint]",
		browseCatalog: "[data-gv2-browse-catalog]",
		brandLogos: "[data-gv2-brand-logos]",
		// Empty state
		emptyState: "[data-gv2-empty-state]",
		addVehicleEmpty: "[data-gv2-add-vehicle-empty]",
	};

	/* ---- State ---- */
	let isOpen = false;
	let editState = null; // { ymm, index } when editing
	let currentActiveVehicle = null; // the vehicle object being previewed
	let imageCache = {}; // handle → image_url
	let bumperdotcommake = false;
	let _vehicleAtOpenTime = null; // snapshot for PDP reload-on-change

	/* ---- Timer IDs (cleared on close) ---- */
	let _manualBtnTimer = null;
	let _fitmentPollTimer = null;

	/* ---- DOM refs (set in init) ---- */
	let $overlay,
		$modal,
		$garageView,
		$addView,
		$vehicleList,
		$backBtn,
		$formTitle;
	let $easysearchSlot, $manualSearch;
	let $vinInput, $vinSubmit, $vinError;
	let $licenseState, $licensePlate, $licenseSubmit, $licenseError;
	let $previewImage,
		$preview,
		$spinner,
		$previewTitle,
		$previewVin,
		$previewPaint,
		$browseCatalog,
		$brandLogos,
		$emptyState;

	const cfg = () => window.__gv2Config || {};
	const i18n = (key) => (cfg().i18n || {})[key] || key;

	/* ==================================================================
     INIT
     ================================================================== */
	function init() {
		$overlay = document.querySelector(SEL.overlay);
		if (!$overlay) return;

		$modal = $overlay.querySelector(SEL.modal);
		$garageView = $overlay.querySelector(SEL.garageView);
		$addView = $overlay.querySelector(SEL.addView);
		$vehicleList = $overlay.querySelector(SEL.vehicleList);
		$backBtn = $overlay.querySelector(SEL.backBtn);
		$formTitle = $overlay.querySelector(SEL.formTitle);
		$easysearchSlot = $overlay.querySelector(SEL.easysearchSlot);
		$manualSearch = $overlay.querySelector(SEL.manualSearch);
		$vinInput = $overlay.querySelector(SEL.vinInput);
		$vinSubmit = $overlay.querySelector(SEL.vinSubmit);
		$vinError = $overlay.querySelector(SEL.vinError);
		$licenseState = $overlay.querySelector(SEL.licenseState);
		$licensePlate = $overlay.querySelector(SEL.licensePlate);
		$licenseSubmit = $overlay.querySelector(SEL.licenseSubmit);
		$licenseError = $overlay.querySelector(SEL.licenseError);
		$previewImage = $overlay.querySelector(SEL.previewImage);
		$preview = $overlay.querySelector(SEL.preview);
		$spinner = $overlay.querySelector(SEL.spinner);
		$previewTitle = $overlay.querySelector(SEL.previewTitle);
		$previewVin = $overlay.querySelector(SEL.previewVin);
		$previewPaint = $overlay.querySelector(SEL.previewPaint);
		$browseCatalog = $overlay.querySelector(SEL.browseCatalog);
		$brandLogos = $overlay.querySelector(SEL.brandLogos);
		$emptyState = $overlay.querySelector(SEL.emptyState);

		bindEvents();

		// Let the old popup render off-screen so EasySearch can init its dropdowns
		const oldPopup = document.querySelector(".garage-easyYMM-popup");
		if (oldPopup && oldPopup.classList.contains("hidden")) {
			oldPopup.classList.remove("hidden");
			oldPopup.classList.add("gv2-offscreen");
		}

		ensureEasySearchMoved();
		monitorManualSearchButton();
		monitorFitmentChanges();

		// Override the global toggleGaragePopup
		window.toggleGaragePopup = toggle;

		// Expose stable namespaced API
		window.GarageV2 = { open: open, close: close, toggle: toggle };

		// Bind header garage buttons (#easyBtn and #easyBtnMobile) to open V2 modal
		bindHeaderGarageButtons();

		// Listen for external searchTerms changes (e.g. from hero banner or other entry points)
		window.addEventListener("searchTermsUpdated", handleExternalTermsUpdate);

		// NOTE: gv2-active (hides old popup) is added inside moveEasySearch()
		// after the widget is successfully moved — not here, to avoid
		// hiding the container before EasySearch can render its dropdowns.

		/* ── Init-time vehicle bridge sync ──
		   Emit one partify:vehicle:changed event so any page-level listeners
		   (PDP gating, homepage hub, browse page) pick up the current state. */
		const initTerms = getTerms();
		if (initTerms.length) {
			syncActiveVehicle(initTerms[0]);
		} else {
			syncActiveVehicle(null);
		}
	}

	/* ==================================================================
     OPEN / CLOSE
     ================================================================== */
	function toggle() {
		if (isOpen) close();
		else open();
	}

	function open() {
		if (isOpen) return;
		isOpen = true;
		// Snapshot first vehicle so close() can detect changes on PDP
		const t = getTerms();
		_vehicleAtOpenTime = t.length ? t[0].ymm : null;
		ensureEasySearchMoved();
		monitorManualSearchButton(); // restart on each open (cleaned up on close)
		const scrollY = window.scrollY;
		document.body.dataset.gv2ScrollY = scrollY;

		// Scroll lock
		let lockStyle = document.getElementById("gv2-lock-style");
		if (!lockStyle) {
			lockStyle = document.createElement("style");
			lockStyle.id = "gv2-lock-style";
			document.head.appendChild(lockStyle);
		}
		lockStyle.textContent = `body.gv2-locked{position:fixed;top:-${scrollY}px;left:0;width:100%;overflow:hidden!important;}`;
		document.body.classList.add("gv2-locked");

		$overlay.classList.remove("hidden");

		// Decide which view to show — single getTerms() call
		const terms = getTerms();
		if (terms.length) {
			showView("garage", terms);
			renderVehicleList(terms);
			hydratePreview(terms[0]);
			toggleEmptyState(false);
		} else {
			// Skip empty-state truck animation — go straight to add form
			editState = null;
			showView("add");
			toggleEmptyState(false);
			clearPreview();
			ensureWidgetInSlot();
			resetEasysearchFields();
		}

		// Hide old Shopify chat
		if (typeof hideShopifyChat === "function") hideShopifyChat();
	}

	function close() {
		if (!isOpen) return;
		isOpen = false;
		$overlay.classList.add("hidden");

		const savedY = parseInt(document.body.dataset.gv2ScrollY || "0", 10);
		document.body.classList.remove("gv2-locked");
		const lockStyle = document.getElementById("gv2-lock-style");
		if (lockStyle) lockStyle.remove();
		window.scrollTo(0, savedY);

		editState = null;
		if (window.editCarState) window.editCarState.open = false;

		cleanupTimers();
		if (typeof showShopifyChat === "function") showShopifyChat();

		// On product pages, reload if the active vehicle changed so the
		// fitment widget, Add-to-Cart gating, and paint options all
		// re-evaluate against the correct vehicle.
		if (window.location.pathname.includes("/products/")) {
			const cur = getTerms();
			const curYmm = cur.length ? cur[0].ymm : null;
			if (curYmm !== _vehicleAtOpenTime) {
				window.location.reload();
			}
		}
	}

	function cleanupTimers() {
		if (_manualBtnTimer) {
			clearInterval(_manualBtnTimer);
			_manualBtnTimer = null;
		}
		// Note: _fitmentPollTimer keeps running — it monitors background changes
	}

	/* ==================================================================
     VIEW SWITCHING
     ================================================================== */
	function showView(name, termsOpt) {
		if (name === "garage") {
			$garageView.style.display = "";
			$addView.style.display = "none";
			$backBtn.style.display = "none";
		} else {
			$garageView.style.display = "none";
			$addView.style.display = "";
			const terms = termsOpt || getTerms();
			$backBtn.style.display = terms.length > 0 ? "" : "none";
			if ($formTitle) {
				$formTitle.textContent = editState
					? i18n("editVehicle")
					: i18n("selectVehicle");
			}
		}
	}

	/* ==================================================================
     EMPTY STATE — truck loader toggle
     ================================================================== */
	function toggleEmptyState(show) {
		if (!$emptyState) return;
		if (show) {
			$emptyState.style.display = "";
			if ($vehicleList) $vehicleList.style.display = "none";
			// Hide the "Add Vehicle" dashed button since the CTA is inside empty state
			const addBtn = $overlay.querySelector(SEL.addVehicleBtn);
			if (addBtn) addBtn.style.display = "none";
		} else {
			$emptyState.style.display = "none";
			if ($vehicleList) $vehicleList.style.display = "";
			const addBtn = $overlay.querySelector(SEL.addVehicleBtn);
			if (addBtn) addBtn.style.display = "";
		}
	}

	/* ==================================================================
     TABS
     ================================================================== */
	function activateTab(tabName) {
		const allTabs = $overlay.querySelectorAll(SEL.tab);
		const allPanels = $overlay.querySelectorAll(SEL.panel);
		allTabs.forEach((t) =>
			t.classList.toggle("active", t.dataset.gv2Tab === tabName),
		);
		allPanels.forEach((p) =>
			p.classList.toggle("active", p.dataset.gv2Panel === tabName),
		);
	}

	/* ==================================================================
     VEHICLE LIST RENDERING
     ================================================================== */
	function renderVehicleList(terms) {
		if (!$vehicleList) return;

		var newHtml = terms
			.slice(0, 5)
			.map((v, i) => {
				const isActive = i === 0;
				const cls = isActive ? " active" : "";
				const vinHtml = v.vin
					? `<span class="gv2-vehicle-item__meta">VIN: ${escHtml(v.vin)}</span>`
					: "";
				// Build paint display: code + description, code only, description only, or hide
				let paintLabel = "";
				if (
					v.paintCode &&
					v.paintCode !== ".VIN" &&
					v.troublesome_description
				) {
					paintLabel = `${v.paintCode} (${v.troublesome_description})`;
				} else if (v.paintCode && v.paintCode !== ".VIN") {
					paintLabel = v.paintCode;
				} else if (v.troublesome_description) {
					paintLabel = v.troublesome_description;
				}
				const paintHtml = paintLabel
					? `<span class="gv2-vehicle-item__meta">Paint: ${escHtml(paintLabel)}</span>`
					: "";
				const metaHtml =
					vinHtml || paintHtml
						? `<div class="gv2-vehicle-item__metas">${vinHtml}${paintHtml}</div>`
						: "";
				const ctaHtml = isActive
					? `<button class="gv2-vehicle-item__cta" type="button" data-gv2-action="shop" data-gv2-idx="${i}" data-garage-active-cta>Shop Parts for this Vehicle</button>`
					: "";

				return `
        <li class="gv2-vehicle-item${cls}" data-gv2-vi="${i}" data-gv2-ymm="${escAttr(v.ymm)}" data-garage-vehicle>
          <div class="gv2-vehicle-item__info">
            <div class="gv2-vehicle-item__name">${escHtml(v.ymm)}</div>
            ${metaHtml}
          </div>
          <button class="gv2-vehicle-item__edit" type="button" data-gv2-action="edit" data-gv2-idx="${i}" aria-label="Edit vehicle">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
          </button>
          <button class="gv2-vehicle-item__delete" type="button" data-gv2-action="delete" data-gv2-idx="${i}" aria-label="Remove vehicle">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 4V2h10v2h5v2h-2v15a1 1 0 01-1 1H5a1 1 0 01-1-1V6H2V4h5zM6 6v14h12V6H6zm3 3h2v8H9V9zm4 0h2v8h-2V9z"/></svg>
          </button>
          ${ctaHtml}
        </li>`;
			})
			.join("");

		/* If the HTML is identical, skip the DOM swap entirely */
		if ($vehicleList.innerHTML.trim() === newHtml.trim()) return;

		/* Direct swap — no animation needed. Redundant re-renders are
		   already blocked by _internalActionInProgress, so this only
		   fires once per user action. */
		$vehicleList.innerHTML = newHtml;
	}

	/* ==================================================================
     VEHICLE PREVIEW CARD
     ================================================================== */
	function hydratePreview(vehicle) {
		if (!vehicle) {
			clearPreview();
			return;
		}
		currentActiveVehicle = vehicle;

		const ymm = vehicle.ymm || "";
		const shortTitle =
			[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ") ||
			ymm;

		// Swap: hide brand logos, show preview panel
		if ($brandLogos) $brandLogos.classList.add("hidden");
		if ($preview) $preview.style.display = "";

		/* Only apply the fade-out transition when the image will need a
		   network fetch. For cached images the preload in setPreviewImage
		   completes near-instantly so fading out just causes a needless
		   flicker. */
		var _previewHandle = [vehicle.year, vehicle.make, vehicle.model]
			.filter(Boolean)
			.join(" ")
			.trim()
			.toLowerCase()
			.replace(/[./]/g, "-")
			.replace(/\s+/g, "-")
			.replace(/[^\w\-]+/g, "")
			.replace(/--+/g, "-")
			.replace(/-+$/, "");
		if (
			!imageCache[_previewHandle] &&
			$preview &&
			$preview.classList.contains("gv2-preview--ready")
		) {
			$preview.classList.add("gv2-preview--switching");
		}

		// Show info
		$previewTitle.style.display = "";
		$previewTitle.textContent = shortTitle;

		if (vehicle.vin) {
			$previewVin.style.display = "";
			$previewVin.textContent = "VIN: " + vehicle.vin;
		} else {
			$previewVin.style.display = "none";
		}

		if (
			vehicle.paintCode &&
			vehicle.paintCode !== ".VIN" &&
			vehicle.troublesome_description
		) {
			$previewPaint.style.display = "";
			$previewPaint.textContent =
				"Paint: " +
				vehicle.paintCode +
				" (" +
				vehicle.troublesome_description +
				")";
		} else if (vehicle.paintCode && vehicle.paintCode !== ".VIN") {
			$previewPaint.style.display = "";
			$previewPaint.textContent = "Paint Code: " + vehicle.paintCode;
		} else if (vehicle.troublesome_description) {
			$previewPaint.style.display = "";
			$previewPaint.textContent = "Paint: " + vehicle.troublesome_description;
		} else {
			$previewPaint.style.display = "none";
		}

		// Browse button is now inside the vehicle card (data-garage-active-cta)
		// Right-panel browse kept hidden; CTA lives in the active vehicle item.

		// Fetch image
		fetchVehicleImage(vehicle);
	}

	function clearPreview() {
		currentActiveVehicle = null;
		$previewTitle.style.display = "none";
		$previewVin.style.display = "none";
		$previewPaint.style.display = "none";
		$previewImage.classList.remove("loaded", "blueprint");
		$previewImage.src = "";
		$spinner.classList.remove("active");
		$browseCatalog.classList.remove("visible");

		// Swap: hide preview panel, show brand logos
		if ($preview) $preview.style.display = "none";
		if ($brandLogos) $brandLogos.classList.remove("hidden");
	}

	async function fetchVehicleImage(vehicle) {
		const year = (vehicle.year || "").toString().trim();
		const make = (vehicle.make || "").toString().trim();
		const model = (vehicle.model || "").toString().trim();
		const titleShort = [year, make, model].filter(Boolean).join(" ").trim();
		const fallback = cfg().fallbackImageUrl || "";

		// DEBUG: Log fallback URL
		console.log("[Garage Debug] Fallback image URL:", fallback);

		if (!titleShort) {
			// No year/make/model - show fallback immediately if available
			if (fallback) {
				setPreviewImage(fallback, "");
			} else {
				$spinner.classList.remove("active");
			}
			return;
		}

		const handle = titleShort
			.toLowerCase()
			.replace(/[./]/g, "-")
			.replace(/\s+/g, "-")
			.replace(/[^\w\-]+/g, "")
			.replace(/--+/g, "-")
			.replace(/-+$/, "");

		// Check cache
		if (imageCache[handle]) {
			setPreviewImage(imageCache[handle], titleShort);
			return;
		}

		// Show loading
		$spinner.classList.add("active");

		try {
			const res = await fetch(
				"/collections/" + handle + "?view=vehicle-image",
				{ credentials: "same-origin" },
			);
			if (!res.ok) throw new Error(res.status);
			const data = JSON.parse(await res.text());
			console.log("[Garage Debug] Collection image fetch result:", data);
			if (data.image && !looksLikePartImage(data.image)) {
				imageCache[handle] = data.image;
				setPreviewImage(data.image, data.title || titleShort);
			} else if (fallback) {
				// Use blueprint fallback
				console.log("[Garage Debug] Using fallback image");
				imageCache[handle] = fallback;
				setPreviewImage(fallback, titleShort);
			} else {
				console.log("[Garage Debug] No image or fallback available");
				$spinner.classList.remove("active");
			}
		} catch (e) {
			// Network error or missing collection — try fallback
			if (fallback) {
				imageCache[handle] = fallback;
				setPreviewImage(fallback, titleShort);
			} else {
				$spinner.classList.remove("active");
			}
		}
	}

	function setPreviewImage(src, alt) {
		const fallback = cfg().fallbackImageUrl || "";
		const isBlueprintImg = fallback && src === fallback;

		/* If the image is already showing this exact src, skip — no work. */
		var currentSrc = $previewImage.getAttribute("src") || "";
		if (currentSrc === src) {
			$previewImage.classList.add("loaded");
			$previewImage.classList.toggle("blueprint", isBlueprintImg);
			$spinner.classList.remove("active");
			if ($preview) {
				$preview.classList.remove("gv2-preview--switching");
				$preview.classList.add("gv2-preview--ready");
			}
			return;
		}

		/* Preload the new image in a detached <img> so the old image stays
		   fully visible until the new one is ready — zero blank frames.
		   But if there's no current image showing, skip the preload and
		   set src directly so we don't stare at a blank preview. */
		var hasCurrent = $previewImage.getAttribute("src");
		if (!hasCurrent) {
			$previewImage.onload = function () {
				$previewImage.classList.add("loaded");
				$previewImage.classList.toggle("blueprint", isBlueprintImg);
				$spinner.classList.remove("active");
				if ($preview) {
					$preview.classList.remove("gv2-preview--switching");
					$preview.classList.add("gv2-preview--ready");
				}
			};
			$previewImage.onerror = function () {
				/* Even on error, mark as loaded so the image (or broken
				   icon) is visible instead of staying at opacity 0 */
				$previewImage.classList.add("loaded");
				$previewImage.classList.toggle("blueprint", isBlueprintImg);
				$spinner.classList.remove("active");
				if ($preview) {
					$preview.classList.remove("gv2-preview--switching");
					$preview.classList.add("gv2-preview--ready");
				}
			};
			$previewImage.src = src;
			$previewImage.alt = alt || "";
			return;
		}

		var tempImg = new Image();
		tempImg.onload = function () {
			/* Swap src only after new image is decoded / cached */
			$previewImage.src = src;
			$previewImage.alt = alt || "";
			$previewImage.classList.add("loaded");
			$previewImage.classList.toggle("blueprint", isBlueprintImg);
			$spinner.classList.remove("active");
			if ($preview) {
				$preview.classList.remove("gv2-preview--switching");
				$preview.classList.add("gv2-preview--ready");
			}
		};
		tempImg.onerror = function () {
			/* Still set the src so the fallback/blueprint shows even if
			   the preload reported an error (e.g. CORS, redirect). */
			$previewImage.src = src;
			$previewImage.alt = alt || "";
			$previewImage.classList.add("loaded");
			$previewImage.classList.toggle("blueprint", isBlueprintImg);
			$spinner.classList.remove("active");
			if ($preview) {
				$preview.classList.remove("gv2-preview--switching");
				$preview.classList.add("gv2-preview--ready");
			}
		};
		/* Don't remove 'loaded' — keep old image visible while new one loads */
		tempImg.src = src;
	}

	function looksLikePartImage(src) {
		var s = String(src || "").toLowerCase();
		var kw = [
			"bumper",
			"fender",
			"grille",
			"fog",
			"bracket",
			"support",
			"insert",
			"bezel",
			"deflector",
			"mirror",
			"door",
			"panel",
			"hood",
			"cover",
			"valance",
			"radiator",
			"headlight",
			"taillight",
			"painted",
			"painted-to-match",
			"ships-unfolded",
			"shipped-unfolded",
			"hardware",
			"kit",
		];
		for (var i = 0; i < kw.length; i++) {
			if (s.indexOf(kw[i]) > -1) return true;
		}
		return false;
	}

	/* ==================================================================
     MOVE EASYSEARCH WIDGET
     ================================================================== */
	let _easySearchMoved = false;
	function moveEasySearch() {
		if (_easySearchMoved || !$easysearchSlot) return;
		// The EasySearch widget is inside the old garage popup
		const easyYMM = document.querySelector(".garage-easyYMM-popup .easyYMM");
		if (easyYMM) {
			// Check that the widget actually rendered its dropdowns
			const hasDropdowns = easyYMM.querySelector(
				".easysearch-holder, .easysearch-container, select[id^='easysearch_field']",
			);
			if (!hasDropdowns) return; // widget HTML exists but EasySearch app hasn't rendered yet
			$easysearchSlot.appendChild(easyYMM);
			easyYMM.style.display = "";
			_easySearchMoved = true;
			// NOW safe to hide the old popup since widget is inside our modal
			document.body.classList.add("gv2-active");
			// Label manual dropdown holders once widget is in our DOM
			initManualGridLabels();
		}
	}

	// Retry moving EasySearch widget until it succeeds (covers async widget loading)
	function ensureEasySearchMoved() {
		if (_easySearchMoved) return;
		moveEasySearch();
		if (!_easySearchMoved) {
			let attempts = 0;
			const timer = setInterval(() => {
				attempts++;
				moveEasySearch();
				if (_easySearchMoved || attempts > 80) {
					clearInterval(timer);
					// Fallback: add gv2-active even if move failed after 20s
					if (!_easySearchMoved) {
						console.warn(
							"[GarageV2] EasySearch widget not found after 20s — manual tab may be empty.",
						);
						document.body.classList.add("gv2-active");
					}
				}
			}, 250);
		}
	}

	// Re-check that the widget is still inside the modal slot.
	// EasySearch may re-render its content on fitment change, or the DOM
	// may have shifted.  If the slot is empty, try to pull the widget back.
	function ensureWidgetInSlot() {
		if (!$easysearchSlot) return;
		const existing = $easysearchSlot.querySelector(".easyYMM");
		if (existing) return; // widget still here, nothing to do

		// Widget was lost — reset flag and try again
		_easySearchMoved = false;
		document.body.classList.remove("gv2-active");
		// The old popup might have been recreated by EasySearch; un-hide it offscreen
		const oldPopup = document.querySelector(".garage-easyYMM-popup");
		if (oldPopup) {
			oldPopup.classList.remove("hidden");
			oldPopup.classList.add("gv2-offscreen");
		}
		ensureEasySearchMoved();
	}

	/* ==================================================================
     MANUAL GRID LABELS — assign data-gv2-manual-label to each holder
     so CSS ::before renders "Year", "Make", etc. above each dropdown.
     Idempotent: skips if labels already applied.
     ================================================================== */
	const MANUAL_LABELS = ["Year", "Make", "Model", "Sub Model", "Engine"];
	function initManualGridLabels() {
		if (!$easysearchSlot) return;
		const holders = $easysearchSlot.querySelectorAll(
			".easysearch-select-holder, .easysearch-select-holder-last",
		);
		if (!holders.length) return;
		holders.forEach((el, i) => {
			if (
				i < MANUAL_LABELS.length &&
				!el.hasAttribute("data-gv2-manual-label")
			) {
				el.setAttribute("data-gv2-manual-label", MANUAL_LABELS[i]);
			}
		});
	}

	/* ==================================================================
     MANUAL SEARCH BUTTON SYNC
     ================================================================== */
	function getManualSelects() {
		// Scope to the V2 modal slot — avoids grabbing header EasySearch dupes
		const root = $easysearchSlot || $overlay;
		if (!root) return null;
		const y = root.querySelector("#easysearch_field_4973");
		const m = root.querySelector("#easysearch_field_4974");
		const d = root.querySelector("#easysearch_field_4975");
		const s = root.querySelector("#easysearch_field_28136");
		const e = root.querySelector("#easysearch_field_28137");
		return { year: y, make: m, model: d, submodel: s, engine: e };
	}

	function monitorManualSearchButton() {
		// Enable Add Vehicle when Year/Make/Model are filled.
		// Scoped to $easysearchSlot so header dupes are ignored.
		if (_manualBtnTimer) clearInterval(_manualBtnTimer);
		_manualBtnTimer = setInterval(() => {
			if (!$manualSearch) return;
			const sel = getManualSelects();
			if (!sel) return;
			const complete = !!(
				sel.year?.value &&
				sel.make?.value &&
				sel.model?.value
			);
			$manualSearch.disabled = !complete;
		}, 600);
	}

	/* ==================================================================
     MONITOR FITMENT CHANGES (same as old garage-script polling)
     ================================================================== */
	function monitorFitmentChanges() {
		const FITMENT_KEY = "easysearch-preselect-fitment";
		const getFitValue = () => {
			try {
				return JSON.parse(localStorage.getItem(FITMENT_KEY))?.value ?? null;
			} catch (e) {
				return null;
			}
		};
		let lastVal = getFitValue();

		if (_fitmentPollTimer) clearInterval(_fitmentPollTimer);
		_fitmentPollTimer = setInterval(() => {
			// Skip view-switch while editing — preselectEasySearch writes
			// fitment localStorage and we must not snap back to garage view.
			if (editState) return;

			const cur = getFitValue();
			if (cur === lastVal) return;
			lastVal = cur;

			try {
				if (!cur) return;

				const ymm = cur
					.replace(/easysearch-preselect-delimiter$/, "")
					.split("easysearch-preselect-delimiter")
					.join(" ")
					.trim();

				if (!ymm) return;

				// Refresh the list & switch to garage view
				const terms = getTerms();
				if (isOpen) {
					// On PDP, auto-close so fitment/paint options re-evaluate
					if (window.location.pathname.includes("/products/")) {
						if (terms.length) syncActiveVehicle(terms[0]);
						close();
						return;
					}
					
					showView("garage", terms);
					toggleEmptyState(!terms.length);
					renderVehicleList(terms);
					if (terms.length) hydratePreview(terms[0]);
				}
				if (terms.length) syncActiveVehicle(terms[0]);
			} catch (e) {
				/* ignore */
			}
		}, 800);
	}

	/* ==================================================================
     EVENT BINDING
     ================================================================== */
	function bindEvents() {
		// Close
		$overlay.querySelector(SEL.close).addEventListener("click", close);
		$overlay.addEventListener("click", (e) => {
			if (e.target === $overlay) close();
		});
		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && isOpen) close();
		});

		// Add Vehicle
		const addBtn = $overlay.querySelector(SEL.addVehicleBtn);
		if (addBtn) {
			addBtn.addEventListener("click", () => {
				editState = null;
				showView("add");
				toggleEmptyState(false);
				clearPreview();
				// Show quick-add section when adding new vehicle
				const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
				const orDivider = $overlay.querySelector("[data-garage-or-divider]");
				if (quickAdd) quickAdd.style.display = "";
				if (orDivider) orDivider.style.display = "";
				// Ensure widget is still in slot (may have been re-rendered by EasySearch app)
				ensureWidgetInSlot();
				resetEasysearchFields();
			});
		}

		// Add Vehicle from empty state CTA
		const addEmptyBtn = $overlay.querySelector(SEL.addVehicleEmpty);
		if (addEmptyBtn) {
			addEmptyBtn.addEventListener("click", () => {
				editState = null;
				showView("add");
				toggleEmptyState(false);
				clearPreview();
				// Show quick-add section when adding new vehicle
				const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
				const orDivider = $overlay.querySelector("[data-garage-or-divider]");
				if (quickAdd) quickAdd.style.display = "";
				if (orDivider) orDivider.style.display = "";
				ensureWidgetInSlot();
				resetEasysearchFields();
			});
		}

		// Back to garage
		if ($backBtn) {
			$backBtn.addEventListener("click", () => {
				editState = null;
				// Restore quick-add section visibility when returning to garage
				const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
				const orDivider = $overlay.querySelector("[data-garage-or-divider]");
				if (quickAdd) quickAdd.style.display = "";
				if (orDivider) orDivider.style.display = "";
				const terms = getTerms();
				showView("garage", terms);
				renderVehicleList(terms);
				toggleEmptyState(!terms.length);
				if (terms.length) hydratePreview(terms[0]);
			});
		}

		// Vehicle list actions (delegation)
		if ($vehicleList) {
			$vehicleList.addEventListener("click", handleVehicleListClick);
		}

		// Tabs (legacy)
		const tabsContainer = $overlay.querySelector(SEL.tabs);
		if (tabsContainer) {
			tabsContainer.addEventListener("click", (e) => {
				const tab = e.target.closest("[data-gv2-tab]");
				if (tab) activateTab(tab.dataset.gv2Tab);
			});
		}

		// Quick-add tab switching (plate ↔ VIN)
		const quickTabs = $overlay.querySelectorAll("[data-gv2-quick-tab]");
		quickTabs.forEach(function (tab) {
			tab.addEventListener("click", function () {
				quickTabs.forEach(function (t) {
					const isTarget = t === tab;
					t.classList.toggle("active", isTarget);
					const text = t.textContent.trim();
					t.innerHTML = isTarget ? "<strong>" + text + "</strong>" : text;
				});
				const quickPanels = $overlay.querySelectorAll("[data-gv2-quick-panel]");
				quickPanels.forEach(function (p) {
					const show = p.dataset.gv2QuickPanel === tab.dataset.gv2QuickTab;
					p.classList.toggle("active", show);
					p.style.display = show ? "" : "none";
				});
			});
		});

		// Manual search button
		if ($manualSearch) {
			$manualSearch.addEventListener("click", handleManualSearch);
		}

		// VIN
		if ($vinInput) {
			$vinInput.addEventListener("input", handleVinInputValidation);
		}
		if ($vinSubmit) {
			$vinSubmit.addEventListener("click", handleVinSubmit);
		}

		// License
		if ($licenseState) {
			$licenseState.addEventListener("change", validateLicenseFields);
		}
		if ($licensePlate) {
			$licensePlate.addEventListener("input", validateLicenseFields);
		}
		if ($licenseSubmit) {
			$licenseSubmit.addEventListener("click", handleLicenseSubmit);
		}

		// Browse catalog from preview
		if ($browseCatalog) {
			$browseCatalog.addEventListener("click", () => {
				if (!currentActiveVehicle) return;
				const handle = generateCollectionHandle(currentActiveVehicle.ymm);
				moveVehicleToFront(currentActiveVehicle);
				window.location.href = "/collections/" + handle;
			});
		}

		// Vehicle item click selects it for preview AND makes it the active vehicle
		if ($vehicleList) {
			$vehicleList.addEventListener("click", (e) => {
				const item = e.target.closest(".gv2-vehicle-item");
				if (!item || e.target.closest("[data-gv2-action]")) return;
				if (e.target.closest(".gv2-vehicle-item__meta")) return;
				const idx = parseInt(item.dataset.gv2Vi, 10);
				const terms = getTerms();
				if (terms[idx]) {
					/* Block handleExternalTermsUpdate from re-rendering
					   while this click handler is still running.
					   The flag is cleared after a short delay so that
					   any async listeners that fire from our
					   moveVehicleToFront / syncActiveVehicle events
					   are also suppressed. */
					_internalActionInProgress = true;

					// Highlight
					$vehicleList
						.querySelectorAll(".gv2-vehicle-item")
						.forEach((li) => li.classList.remove("active"));
					item.classList.add("active");
					hydratePreview(terms[idx]);

					// Move to front so homepage hero, header, etc. all update
					moveVehicleToFront(terms[idx]);
					syncActiveVehicle(terms[idx]);

					// Re-render with updated order
					// (renderVehicleList auto-marks index 0 as active via the template)
					renderVehicleList(getTerms());

					
					// On PDP, auto-close so fitment/paint options re-evaluate
					if (window.location.pathname.includes("/products/")) {
						setTimeout(close, 350);
					}


					// Release the guard after external listeners have fired
					setTimeout(function () {
						_internalActionInProgress = false;
					}, 300);
				}
			});
		}
	}

	/* ==================================================================
     VEHICLE LIST CLICK HANDLER
     ================================================================== */
	function handleVehicleListClick(e) {
		const btn = e.target.closest("[data-gv2-action]");
		if (!btn) return;

		// Isolate action button clicks from parent handlers
		e.preventDefault();
		e.stopPropagation();

		const action = btn.dataset.gv2Action;
		const idx = parseInt(btn.dataset.gv2Idx, 10);
		const terms = getTerms();
		const vehicle = terms[idx];
		if (!vehicle) return;

		if (action === "browse" || action === "shop") {
			// Move to front, then navigate
			moveVehicleToFront(vehicle);
			syncActiveVehicle(vehicle);
			const handle = generateCollectionHandle(vehicle.ymm);
			window.location.href = "/collections/" + handle;
		}

		if (action === "edit") {
			editState = { ymm: vehicle.ymm, index: idx };
			window.editCarState = {
				open: true,
				ymm: vehicle.ymm,
				url: "/collections/" + generateCollectionHandle(vehicle.ymm),
			};

			showView("add");
			hydratePreview(vehicle);

			// Hide quick-add section (VIN/License) when editing
			const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
			const orDivider = $overlay.querySelector("[data-garage-or-divider]");
			if (quickAdd) quickAdd.style.display = "none";
			if (orDivider) orDivider.style.display = "none";

			// Ensure manual section is visible and widget is in slot
			ensureWidgetInSlot();

			// Pre-populate EasySearch fields
			preselectEasySearch(vehicle);
		}

		if (action === "delete") {
			deleteVehicle(vehicle, idx);
		}
	}

	/* ==================================================================
     DELETE VEHICLE — handles all indices (no page reload)
     ================================================================== */
	function deleteVehicle(vehicle, idx) {
		_internalActionInProgress = true;
		let terms = getTerms();
		const wasFirst = idx === 0;

		// Remove from array
		terms = terms.filter((v) => v.ymm !== vehicle.ymm);

		// Persist — setSearchTerms triggers vh:searchTerms for hero banner
		setSearchTerms(terms, 9);

		// If removing last vehicle, set force-clear flag for EasySearch
		if (terms.length === 0) {
			try {
				sessionStorage.setItem("es_force_clear", "1");
			} catch (e) {}
		}

		// Update fitment object to reflect new first (or empty)
		if (terms.length > 0) {
			const first = terms[0];
			const fields = [
				first.year,
				first.make,
				first.model,
				first.submodel,
				first.engine,
			];
			const fitVal = fields
				.filter((f) => f && f.trim())
				.map((f) => f.trim() + "easysearch-preselect-delimiter")
				.join("");
			try {
				setFitmentObject({
					value: fitVal,
					expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
				});
			} catch (e) {}
		} else {
			try {
				localStorage.setItem(
					"easysearch-preselect-fitment",
					JSON.stringify({ value: "" }),
				);
			} catch (e) {}
		}

		// Update the V2 modal UI instantly
		if (terms.length === 0) {
			showView("garage");
			toggleEmptyState(true);
			editState = null;
			clearPreview();
			syncActiveVehicle(null);
		} else {
			toggleEmptyState(false);
			renderVehicleList(terms);
			hydratePreview(terms[0]);
			syncActiveVehicle(terms[0]);
		}
		// Release the guard after external listeners have fired
		setTimeout(function () {
			_internalActionInProgress = false;
		}, 300);
	}

	/* ==================================================================
     MANUAL SEARCH
     ================================================================== */
	function handleManualSearch() {
		const sel = getManualSelects();
		if (!sel) return;
		const { year, make, model: mdl, submodel, engine } = sel;

		if (!year?.value || !make?.value || !mdl?.value) return;

		const yearVal = year.value;
		const makeVal = make.value;
		const modelVal = mdl.value;
		const submodelVal = submodel?.value || "";
		const engineVal = engine?.value || "";

		let newVin = null;
		let newPaintCode = null;
		let newDecodedByBumper = null;
		let newLastCollectionImage = null;

		// Edit mode — preserve VIN / paint from the vehicle being edited
		if (editState && window.editCarState && window.editCarState.open) {
			const terms = getTerms();
			const idx = terms.findIndex(
				(t) => (t.ymm || "").trim() === window.editCarState.ymm.trim(),
			);
			if (idx >= 0) {
				newVin = terms[idx].vin;
				newPaintCode = terms[idx].paintCode;
				newDecodedByBumper = terms[idx].decodedByBumper;
				newLastCollectionImage = terms[idx].lastCollectionImage;
			}
		}

		if (typeof logGarageEventToSheets === "function") {
			logGarageEventToSheets("dropdown", {});
		}

		// Save vehicle to garage (localStorage + account metafield)
		const ymm = [yearVal, makeVal, modelVal, submodelVal, engineVal]
			.filter(Boolean)
			.join(" ");

		if (editState && window.editCarState && window.editCarState.open) {
			updateSearchTerms({
				ymm: window.editCarState.ymm,
				year: yearVal,
				make: makeVal,
				model: modelVal,
				submodel: submodelVal,
				engine: engineVal,
				vin: newVin,
				paintCode: newPaintCode,
				decodedByBumper: newDecodedByBumper,
				lastCollectionImage: newLastCollectionImage,
				troublesome_format: null,
				troublesome_description: null,
			});

			/* ── Edit-mode save: stay inside modal ──
			   Write fitment, refresh the list, and return
			   without navigating or reloading the page. */
			const fields = [yearVal, makeVal, modelVal, submodelVal, engineVal];
			const fitVal = fields
				.filter(Boolean)
				.map((f) => f + "easysearch-preselect-delimiter")
				.join("");
			setFitmentObject({
				value: fitVal,
				expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
			});

			const updatedTerms = getTerms();
			editState = null;
			window.editCarState = { open: false };

			// Restore quick-add visibility for future add flows
			const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
			const orDivider = $overlay.querySelector("[data-garage-or-divider]");
			if (quickAdd) quickAdd.style.display = "";
			if (orDivider) orDivider.style.display = "";

			showView("garage", updatedTerms);
			renderVehicleList(updatedTerms);
			toggleEmptyState(!updatedTerms.length);
			if (updatedTerms.length) {
				hydratePreview(updatedTerms[0]);
				syncActiveVehicle(updatedTerms[0]);
			}
			return;
		} else {
			moveVehicleToFront({
				ymm: ymm,
				year: yearVal,
				make: makeVal,
				model: modelVal,
				submodel: submodelVal,
				engine: engineVal,
				vin: newVin,
				paintCode: newPaintCode,
				decodedByBumper: newDecodedByBumper,
				lastCollectionImage: newLastCollectionImage,
				troublesome_format: null,
				troublesome_description: null,
			});
		}

		// Write fitment localStorage so the page reload picks it up
		const fields = [yearVal, makeVal, modelVal, submodelVal, engineVal];
		const fitVal = fields
			.filter(Boolean)
			.map((f) => f + "easysearch-preselect-delimiter")
			.join("");
		setFitmentObject({
			value: fitVal,
			expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
		});

		const isProductPage = window.location.pathname.includes("/products/");
		close();
		if (isProductPage) {
			window.location.reload();
			return;
		}

		// Non-product pages: navigate to collection
		try {
			updateEasysearchLocalStorageOrNavigate({
				year: yearVal,
				make: makeVal,
				model: modelVal,
				submodel: submodelVal,
				engine: engineVal,
				vin: newVin,
				paintCode: newPaintCode,
				decodedByBumper: newDecodedByBumper,
				lastCollectionImage: newLastCollectionImage,
				troublesome_format: null,
				troublesome_description: null,
			});
		} catch (err) {
			console.warn("[GarageV2] manual search nav error", err);
		}
	}

	/* ==================================================================
     VIN HANDLING
     ================================================================== */
	function handleVinInputValidation() {
		const val = ($vinInput.value || "")
			.trim()
			.toUpperCase()
			.replace(/[^A-HJ-NPR-Z0-9]/g, "");
		$vinInput.value = val;
		$vinSubmit.disabled = val.length !== 17;
		$vinError.textContent = "";
	}

	async function handleVinSubmit() {
		const vin = ($vinInput.value || "").trim().toUpperCase();
		if (vin.length !== 17) {
			$vinError.textContent = i18n("exact17");
			return;
		}

		// Loading state
		$vinSubmit.disabled = true;
		$vinSubmit.classList.add("gv2-btn-loading");
		/* Keep textContent so button retains its intrinsic width;
		   gv2-btn-loading hides label via color:transparent */
		$vinError.textContent = "";

		_internalActionInProgress = true;
		try {
			// Track attempts
			if (typeof attemptedDecodedVins !== "undefined")
				attemptedDecodedVins.push(vin);
			if (typeof attemptedVinsInGarage !== "undefined")
				attemptedVinsInGarage.push(vin);

			const result = await handleVinDecode(
				vin,
				"",
				"",
				1,
				bumperdotcommake,
				i18n("useYMMDropdown"),
				i18n("noResults"),
				i18n("search"),
				"",
				"",
				"",
				"",
				"",
				"",
				"",
			);
			close();

			const terms = getTerms();
			const isProductPage = window.location.pathname.includes("/products/");
			if (isProductPage) {
				window.location.reload();
				return;
			}
			if (
				terms.length > 0 &&
				terms[0].ymm &&
				typeof generateCollectionHandle === "function"
			) {
				const handle = generateCollectionHandle(terms[0].ymm);
				if (handle) {
					window.location.assign("/collections/" + handle);
					return;
				}
			}
		} catch (err) {
			$vinError.textContent = i18n("failedVin");
		} finally {
			_internalActionInProgress = false;
			$vinSubmit.disabled = false;
			$vinSubmit.classList.remove("gv2-btn-loading");
			$vinSubmit.textContent = "ADD VEHICLE";
			$vinInput.value = "";
		}
	}

	/* ==================================================================
     LICENSE PLATE HANDLING
     ================================================================== */
	function validateLicenseFields() {
		if (!$licenseState || !$licensePlate || !$licenseSubmit) return;
		const st = $licenseState.value;
		const raw = ($licensePlate.value || "").toUpperCase();
		if ($licensePlate.value !== raw) $licensePlate.value = raw;
		const pl = raw.trim();
		$licenseSubmit.disabled = !(st && pl.length >= 2);
		if ($licenseError) $licenseError.textContent = "";
	}

	async function handleLicenseSubmit() {
		const state = $licenseState.value;
		const rawPlate = ($licensePlate.value || "").trim();
		if (!state || !rawPlate) return;

		const oRestricted = (cfg().oRestrictedStates || "")
			.split(",")
			.map((s) => s.trim());
		const plate =
			typeof cleanLicensePlate === "function"
				? cleanLicensePlate(state, rawPlate, oRestricted)
				: rawPlate;

		// Check already attempted
		if (
			typeof attemptedDecodedLicensePlates !== "undefined" &&
			attemptedDecodedLicensePlates.includes(state + "-" + plate)
		) {
			$licenseError.textContent = i18n("licensePlateAlreadyAttempted");
			return;
		}

		$licenseSubmit.disabled = true;
		$licenseSubmit.classList.add("gv2-btn-loading");
		/* Keep textContent so button retains its intrinsic width */
		$licenseError.textContent = "";

		_internalActionInProgress = true;
		try {
			if (typeof attemptedDecodedLicensePlates !== "undefined")
				attemptedDecodedLicensePlates.push(state + "-" + plate);

			const data = await fetchBumpersLicenseToVin(state, plate);
			if (!data || !data.vin) {
				$licenseError.textContent = i18n("failedLicense");
				return;
			}

			// Check bumper make
			const currentMake = (data.make || "").toLowerCase();
			bumperdotcommake = false;
			if (
				cfg().displayLicensePlate &&
				(cfg().bumperMakes || "").toLowerCase().includes(currentMake)
			) {
				bumperdotcommake = true;
			}

			// Decode the VIN
			await handleVinDecode(
				data.vin,
				state,
				plate,
				1,
				bumperdotcommake,
				i18n("useYMMDropdown"),
				i18n("failedLicense"),
				i18n("search"),
				"",
				"",
				"",
				"",
				"",
				"",
				"",
			);
			close();

			const terms = getTerms();
			const isProductPage = window.location.pathname.includes("/products/");
			if (isProductPage) {
				window.location.reload();
				return;
			}
			if (
				terms.length > 0 &&
				terms[0].ymm &&
				typeof generateCollectionHandle === "function"
			) {
				const handle = generateCollectionHandle(terms[0].ymm);
				if (handle) {
					window.location.assign("/collections/" + handle);
					return;
				}
			}
		} catch (err) {
			$licenseError.textContent = err.message.includes("already been attempted")
				? i18n("licensePlateAlreadyAttempted")
				: i18n("failedLicense");
		} finally {
			_internalActionInProgress = false;
			$licenseSubmit.disabled = false;
			$licenseSubmit.classList.remove("gv2-btn-loading");
			$licenseSubmit.textContent = "ADD VEHICLE";
			$licensePlate.value = "";
			if ($licenseState) $licenseState.selectedIndex = 0;
		}
	}

	/* ==================================================================
     EASYSEARCH HELPERS
     ================================================================== */
	async function resetEasysearchFields() {
		// Clear fitment preselect
		try {
			setFitmentObject({
				value: "",
				expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
			});
		} catch (e) {}

		const holder =
			document.querySelector(".easysearch-widget .easysearch-container") ||
			document.querySelector(".easysearch-container");
		if (!holder) return;

		const ids = ["4973", "4974", "4975", "28136", "28137"];
		for (const id of ids) {
			const sel = holder.querySelector("#easysearch_field_" + id);
			if (!sel) continue;
			sel.value = "";
			sel.dispatchEvent(new Event("change", { bubbles: true }));
			await wait(20);
		}
		document.dispatchEvent(new Event("easysearch_loaded"));
		document.dispatchEvent(new Event("easysearch_rendered"));
	}

	async function preselectEasySearch(vehicle) {
		const holder =
			document.querySelector(".easysearch-widget .easysearch-container") ||
			document.querySelector(".easysearch-container");
		if (!holder) return;

		const order = ["4973", "4974", "4975", "28136", "28137"];
		const values = [
			vehicle.year,
			vehicle.make,
			vehicle.model,
			vehicle.submodel,
			vehicle.engine,
		];

		const customBtn = document.querySelector(".custom-easysearch-ymm-btn");
		if (customBtn) customBtn.innerHTML = i18n("editBtn");

		for (let i = 0; i < order.length; i++) {
			const val = values[i];
			if (!val) break;
			const sel = holder.querySelector("#easysearch_field_" + order[i]);
			if (!sel) break;
			const opt = Array.from(sel.options).find(
				(o) =>
					o.value.trim().toLowerCase() === String(val).trim().toLowerCase(),
			);
			if (!opt) break;
			sel.value = opt.value;
			sel.dispatchEvent(new Event("change", { bubbles: true }));
			await wait(30);
		}
		document.dispatchEvent(new Event("easysearch_loaded"));
		document.dispatchEvent(new Event("easysearch_rendered"));
	}

	/* ==================================================================
     HELPERS
     ================================================================== */
	function getTerms() {
		try {
			return typeof getSearchTerms === "function"
				? getSearchTerms()
				: JSON.parse(localStorage.getItem("searchTerms") || "[]");
		} catch (e) {
			return [];
		}
	}

	/**
	 * Sync the active vehicle to header, homepage hero bar, and fitment.
	 * Pass null when no vehicle is active (after clearing garage).
	 *
	 * VEHICLE STATE BRIDGE (v2)
	 * ─────────────────────────
	 * Canonical event:  "partify:vehicle:changed" (window, detail = vehicle|null)
	 * Compat signals:   "vh:searchTerms" (legacy hero banner)
	 * Canonical key:    localStorage "searchTerms" (unchanged, written by setSearchTerms/moveVehicleToFront)
	 * Vehicle shape:    { year, make, model, submodel?, engine?, vin?, paintCode?, ymm }
	 *                   vin is always UPPERCASE when present.
	 *
	 * NOTE: We no longer dispatch searchTermsUpdated from here because every
	 * upstream caller (moveVehicleToFront, setSearchTerms) already dispatches it.
	 * Dispatching it AGAIN from syncActiveVehicle was the #1 event amplifier.
	 */
	var _lastSyncedVehicleId = null;
	function syncActiveVehicle(vehicle) {
		/* ── Normalize VIN uppercase ── */
		if (vehicle && vehicle.vin) {
			vehicle.vin = String(vehicle.vin).toUpperCase();
		}

		/* ── Same-vehicle short-circuit ── */
		var batcher = window.__partifyVehicleChangeBatcher;
		var currentId = batcher
			? batcher.normalizeVehicleId(vehicle)
			: JSON.stringify(vehicle);
		var isSameVehicle = currentId === _lastSyncedVehicleId;
		_lastSyncedVehicleId = currentId;

		if (vehicle) {
			const ymm = vehicle.ymm || "";

			// 1. Header garage text (desktop + mobile)
			updateHeaderVehicle(ymm);

			// 2. Homepage garage bar title
			const garageBarTitle = document.getElementById("garage-vehicle-name");
			if (garageBarTitle) garageBarTitle.textContent = ymm;

			// 3. Fitment object — so EasySearch/hero banner pick up the change
			const fields = [
				vehicle.year,
				vehicle.make,
				vehicle.model,
				vehicle.submodel,
				vehicle.engine,
			];
			const fitVal = fields
				.filter((f) => f && String(f).trim())
				.map((f) => String(f).trim() + "easysearch-preselect-delimiter")
				.join("");
			try {
				setFitmentObject({
					value: fitVal,
					expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
				});
			} catch (e) {}

			// 4. Fire legacy hero banner event (only on actual change)
			if (!isSameVehicle) {
				window.dispatchEvent(new Event("vh:searchTerms"));
			}
		} else {
			// No vehicle — reset header
			updateHeaderVehicle("");

			const garageBarTitle = document.getElementById("garage-vehicle-name");
			if (garageBarTitle) garageBarTitle.textContent = "Select Your Vehicle";

			try {
				localStorage.setItem(
					"easysearch-preselect-fitment",
					JSON.stringify({ value: "" }),
				);
			} catch (e) {}

			if (!isSameVehicle) {
				window.dispatchEvent(new Event("vh:searchTerms"));
			}
		}

		/* ── Canonical vehicle-changed event (v2 bridge) ──
		   Skip if vehicle identity hasn't changed (prevents re-entrant storms). */
		if (!isSameVehicle) {
			const payload = vehicle
				? {
						year: vehicle.year || "",
						make: vehicle.make || "",
						model: vehicle.model || "",
						vin: vehicle.vin || null,
						ymm: vehicle.ymm || "",
					}
				: null;
			window.dispatchEvent(
				new CustomEvent("partify:vehicle:changed", { detail: payload }),
			);

			/* Feed the shared batcher so all registered consumers fire once */
			if (batcher) batcher.schedule(vehicle, "syncActiveVehicle");
		}
	}

	function escHtml(s) {
		var d = document.createElement("div");
		d.textContent = s;
		return d.innerHTML;
	}

	function escAttr(s) {
		return String(s || "")
			.replace(/"/g, "&quot;")
			.replace(/</g, "&lt;");
	}

	function wait(ms) {
		return new Promise((r) => setTimeout(r, ms));
	}

	function updateHeaderVehicle(ymm) {
		if (window.GarageUI && window.GarageUI.updateVehicleSelectionElement) {
			window.GarageUI.updateVehicleSelectionElement(ymm);
		}
	}

	/* ==================================================================
     HEADER GARAGE BUTTON BINDING
     Ensures both #easyBtn (desktop) and #easyBtnMobile (mobile)
     open the V2 modal, and keeps both garages in sync.
     ================================================================== */
	function bindHeaderGarageButtons() {
		var easyBtn = document.getElementById("easyBtn");
		if (easyBtn && !easyBtn.dataset.gv2Bound) {
			easyBtn.dataset.gv2Bound = "true";
			easyBtn.addEventListener("click", function (e) {
				e.preventDefault();
				e.stopPropagation();
				toggle();
			});
		}
		// Also bind any elements with data-open-garage attribute
		document.querySelectorAll("[data-open-garage]").forEach(function (el) {
			if (el.dataset.gv2Bound) return;
			el.dataset.gv2Bound = "true";
			el.addEventListener("click", function (e) {
				e.preventDefault();
				open();
			});
		});
	}

	/* ==================================================================
     EXTERNAL SYNC — when something outside V2 changes searchTerms
     (e.g. hero banner, old popup remnant, direct localStorage edit)
     ================================================================== */
	let _externalSyncDebounce = null;
	let _internalActionInProgress = false;
	function handleExternalTermsUpdate() {
		/* If the garage modal itself just dispatched events (via click
		   handler → moveVehicleToFront → setSearchTerms), skip the
		   re-render — the click handler already updated the UI. */
		if (_internalActionInProgress) return;

		/* Skip while editing — dropdown changes fire searchTermsUpdated
		   and we must not snap back to garage view mid-edit. */
		if (editState) return;

		if (_externalSyncDebounce) clearTimeout(_externalSyncDebounce);
		_externalSyncDebounce = setTimeout(function () {
			var terms = getTerms();
			// Update header text
			if (terms.length) {
				updateHeaderVehicle(terms[0].ymm || "");
			} else {
				updateHeaderVehicle("");
			}
			// If V2 modal is open, refresh its view
			if (isOpen) {
				if (terms.length) {
					showView("garage", terms);
					renderVehicleList(terms);
					hydratePreview(terms[0]);
					toggleEmptyState(false);
				} else {
					showView("garage");
					toggleEmptyState(true);
					clearPreview();
				}
			}
		}, 150);
	}

	/* ==================================================================
     BOOTSTRAP
     ================================================================== */
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
